import { t } from 'i18next';
import { Box, Tab, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { useReporting } from '@views/pages/Reporting/Reports/hooks';
import { Dropdown } from '@components';
import { ReportingContext } from '@views/pages/Reporting/Reports/context';
import {
  AddReportFolderModal,
  ReportingPageTable,
} from '@views/pages/Reporting/Reports/components';
import { Link } from 'react-router-dom';
import { Folder, InsertDriveFile } from '@mui/icons-material';
import { formatCount } from '@util/number';

export const Reporting = () => {
  const {
    handleChangeTab,
    dropdownMenu,
    filters,
    handleFilterChange,
    handleResetFilters,
    isReportingSelected,
    isAddReportFolderModalOpen,
    handleCloseAddReportFolderModal,
    isInitialFilters,
    favoriteCount,
    createdByCount,
    allCount,
  } = useReporting();

  const contextValues = {
    filters,
    handleFilterChange,
    handleResetFilters,
    isInitialFilters,
  };

  return (
    <ReportingContext.Provider value={contextValues}>
      <Box
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h4' sx={{ mt: 2, mb: 2 }}>
            {t('reporting.report.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box component='nav' sx={{ display: 'flex' }}>
              <Link to='/reporting/reports'>
                <Box
                  sx={{
                    cursor: 'pointer',
                    height: '48px',
                    width: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    borderBottomLeftRadius: '4px',
                    borderTopLeftRadius: '4px',
                    background: theme =>
                      isReportingSelected
                        ? theme.palette.selectedPageBg.main
                        : 'unset',
                  }}
                >
                  <InsertDriveFile sx={{ height: '24px' }} color='primary' />
                  <Typography variant='body2'>
                    {t('reporting.nav.reports')}
                  </Typography>
                </Box>
              </Link>
              <Link to='/reporting/folders'>
                <Box
                  sx={{
                    cursor: 'pointer',
                    height: '48px',
                    width: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    borderBottomRightRadius: '4px',
                    borderTopRightRadius: '4px',
                    background: theme =>
                      isReportingSelected
                        ? 'unset'
                        : theme.palette.selectedPageBg.main,
                  }}
                >
                  <Folder sx={{ height: '24px' }} color='primary' />
                  <Typography variant='body2'>
                    {t('reporting.nav.folders')}
                  </Typography>
                </Box>
              </Link>
            </Box>
            <Dropdown menuList={dropdownMenu} />
          </Box>
        </Box>
        <TabContext value={filters.tabIndex}>
          <Box>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
                width: '100%',
              }}
            >
              <TabList onChange={handleChangeTab}>
                <Tab label={t('reporting.report.tab.recent')} value='1' />
                <Tab
                  label={`${t('reporting.report.tab.all')} ${allCount > 0 ? '(' + formatCount(allCount) + ')' : ''}`}
                  value='2'
                />
                <Tab
                  label={`${t('reporting.report.tab.favorite')} ${favoriteCount > 0 ? '(' + formatCount(favoriteCount) + ')' : ''}`}
                  value='3'
                />
                <Tab
                  label={`${t('reporting.report.tab.createdByMe')} ${createdByCount > 0 ? '(' + formatCount(createdByCount) + ')' : ''}`}
                  value='4'
                />
              </TabList>
            </Box>
            <>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='1'
              >
                <ReportingPageTable query='recently_viewed' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='2'
              >
                <ReportingPageTable query='all' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='3'
              >
                <ReportingPageTable query='favorites_only' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='4'
              >
                <ReportingPageTable query='created_by_me' />
              </TabPanel>
            </>
          </Box>
        </TabContext>
        <AddReportFolderModal
          isOpen={isAddReportFolderModalOpen}
          handleClose={handleCloseAddReportFolderModal}
        />
      </Box>
    </ReportingContext.Provider>
  );
};
